import React, { Fragment, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  Form,
  DatePicker,
  Button,
  Card,
  Col,
  Row,
  Spin,
  notification,
} from 'antd';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DealershipChooser } from 'components';
import ShopStore from 'stores/ShopStore';
import moment from 'moment';
import dayjs from 'dayjs';
import { FiltersFormWithVVA } from 'components/FleetReports/FiltersFormWithVVA';
import debounce from 'lodash.debounce';
import { useMemo } from 'react';

const H2Styled = styled.h2`
  font-size: 1.2rem;
`;

const AssetAvailability = ({ history, match }) => {
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(3, 'month'), // Default to 3 months ago
    dayjs(), // Default to today
  ]);
  const [filter, setFilter] = useState({
    vendor: [],
    make: [],
    model: [],
    year: [],
    carIds: [],
    assetType: [],
    VMRSSysCode: [],
  });

  const fetchAvailabilityData = async () => {
    const [startDate, endDate] = dateRange;
    const shopId = ShopStore.currentShop?.id;

    if (!shopId || !startDate || !endDate) {
      notification.error({
        message: 'Missing Date Error!',
        description: 'Please select start date and end date to apply filters.',
      });
      return;
    }

    setIsLoadingAvailability(true);

    try {
      const response = await ShopStore.getAssetAvailabilityData(
        shopId,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        filter
      );

      const transformedData = response.data.map((item) => ({
        timeframe: item.timeframe,
        upPercentage: parseFloat(item.uppercentage),
        downPercentage: parseFloat(item.downpercentage),
      }));

      setAvailabilityData(transformedData);
    } catch (error) {
      console.error('Failed to load asset availability data:', error);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const debouncedFetchAvailabilityData = useMemo(
    () => debounce(fetchAvailabilityData, 500),
    [dateRange, filter]
  );

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      debouncedFetchAvailabilityData();
    }

    return () => {
      debouncedFetchAvailabilityData.cancel();
    };
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
  };

  const handleFilterChange = (changed) => {
    if ('dateRange' in changed) {
      setDateRange(changed.dateRange);
    }
    if ('filter' in changed) {
      setFilter(changed.filter);
    }
  };

  const handleResetFilters = () => {
    setFilter({
      vendor: [],
      make: [],
      model: [],
      year: [],
      carIds: [],
      assetType: [],
      VMRSSysCode: [],
    });
    setDateRange([dayjs().subtract(3, 'month'), dayjs()]);
    fetchAvailabilityData();
  };

  const handleApplyFilters = () => {
    fetchAvailabilityData();
  };

  const chartOptions = {
    chart: {
      type: 'column',
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: availabilityData.map((item) => item.timeframe),
      title: {
        text: 'Timeframe',
      },
    },
    yAxis: {
      min: 0,
      max: 100,
      title: {
        text: '% of Up vs Down',
      },
      labels: {
        format: '{value}%',
      },
    },
    plotOptions: {
      column: {
        stacking: 'percent',
        dataLabels: {
          enabled: true,
          formatter: function () {
            return `${this.y}%`;
          },
        },
      },
    },
    series: [
      {
        name: 'Up',
        data: availabilityData.map((item) => item.upPercentage),
        color: '#183cbc',
      },
      {
        name: 'Down',
        data: availabilityData.map((item) => item.downPercentage),
        color: '#e63946',
      },
    ],
  };

  return (
    <Fragment>
      <Row gutter={[8, 8]} justify="space-between" align="middle">
        <Col span={20}>
          <h1>Asset Availability</h1>
        </Col>
      </Row>

      <DealershipChooser />

      <FiltersFormWithVVA
        filter={{
          filter: { ...filter },
        }}
        dateRange={dateRange}
        onChange={handleFilterChange}
        onReset={handleResetFilters}
        onApply={handleApplyFilters}
        hideVMRSFilter={true}
        hideStatusFilter={true}
        hideLimitFilter={true}
      />

      <Row style={{ marginTop: '10px' }} gutter={[8, 8]}>
        <Col span={24}>
          <Spin spinning={isLoadingAvailability}>
            <Card>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  {availabilityData.length > 0 ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={chartOptions}
                    />
                  ) : (
                    <p>No data available for the selected filters.</p>
                  )}
                </Col>
              </Row>
            </Card>
          </Spin>
        </Col>
      </Row>
    </Fragment>
  );
};

AssetAvailability.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object,
};

export default withRouter(observer(AssetAvailability));
